import type { PlanResult } from "@decopl/deco-engine";
import { tankPressureFromLiters, totalConsumption } from "@decopl/deco-engine";
import { STR } from "../i18n.js";
import { useStore } from "../store.js";
import { AlertTriangle } from "./Icons.js";

interface Props {
  result: PlanResult | null;
  error: string | null;
}

type Tone = "safe" | "caution" | "warn" | "danger" | "accent";

function Metric({
  label,
  value,
  unit,
  tone,
  sub,
}: {
  label: string;
  value: string | number;
  unit?: string;
  tone?: Tone;
  sub?: string;
}) {
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className={`metric-value${tone ? ` ${tone}` : ""}`}>
        {value}
        {unit && <span className="unit">{unit}</span>}
      </div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

export function ResultsPanel({ result, error }: Props) {
  const { sac, tankVolume, tankStartPressure, lang } = useStore();
  const t = STR[lang];

  if (error) {
    return (
      <div className="panel">
        <div className="panel-head">
          <h3>{t.results}</h3>
        </div>
        <div className="panel-body">
          <div className="warn-banner">
            <AlertTriangle size={14} />
            {error}
          </div>
        </div>
      </div>
    );
  }
  if (!result) return null;

  const cons = totalConsumption(result.segments, sac);
  const endP = tankPressureFromLiters(cons, {
    volume: tankVolume,
    startPressure: tankStartPressure,
  });

  let descT = 0,
    ascT = 0,
    stopT = 0;
  for (const s of result.segments) {
    if (s.kind === "descent") descT += s.duration;
    else if (s.kind === "ascent") ascT += s.duration;
    else if (s.kind === "stop") stopT += s.duration;
  }

  const ndlVal = result.noStopLimitAtMaxDepth;
  const ndlExceeded = ndlVal !== null && result.bottomTime > ndlVal;
  const cnsTone: Tone =
    result.cnsPercent > 80 ? "danger" : result.cnsPercent > 50 ? "caution" : "safe";
  const otuTone: Tone = result.otu > 300 ? "danger" : result.otu > 200 ? "caution" : "safe";
  const endTone: Tone = endP < 50 ? "danger" : endP < 70 ? "caution" : "safe";

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>{t.results}</h3>
      </div>
      <div className="panel-body flush">
        <div className="metrics">
          <Metric
            label={`${t.ndl} @ ${result.maxDepth.toFixed(0)}m`}
            value={ndlVal === null ? "∞" : ndlVal}
            unit="min"
            tone={ndlExceeded ? "caution" : "safe"}
            sub={`${result.bottomTime.toFixed(0)} min ${t.planned}`}
          />
          <Metric
            label={t.tts}
            value={result.timeToSurface.toFixed(0)}
            unit="min"
            tone="accent"
            sub={
              stopT > 0
                ? `${stopT.toFixed(0)} min ${t.stops}`
                : t.no_deco.split("—")[0]?.trim() ?? ""
            }
          />
          <Metric label={t.cns} value={result.cnsPercent.toFixed(0)} unit="%" tone={cnsTone} />
          <Metric label={t.otu} value={result.otu.toFixed(0)} tone={otuTone} />
          <Metric
            label={t.gas_used}
            value={cons.toFixed(0)}
            unit="L"
            tone="accent"
            sub={lang === "fr" ? "à la surface" : "at surface"}
          />
          <Metric
            label={t.gas_end}
            value={endP.toFixed(0)}
            unit="bar"
            tone={endTone}
            sub={`${tankStartPressure} → ${endP.toFixed(0)}`}
          />
        </div>
      </div>
    </div>
  );
}

export function DecoSchedule({ result }: { result: PlanResult | null }) {
  const { gases, lang } = useStore();
  const t = STR[lang];
  if (!result) return null;

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>{t.deco}</h3>
      </div>
      <div className="panel-body flush">
        {result.isNoDeco ? (
          <div className="no-deco">
            <span className="no-deco-dot" />
            {t.no_deco}
          </div>
        ) : (
          <div className="deco-table">
            <div className="deco-row head">
              <span />
              <span>{lang === "fr" ? "Profondeur" : "Depth"}</span>
              <span>{lang === "fr" ? "Durée" : "Duration"}</span>
              <span>{lang === "fr" ? "Gaz" : "Gas"}</span>
            </div>
            {result.decoStops.map((s, i) => {
              const g = gases[s.gasIdx] ?? { fO2: 0.21 };
              const o2 = Math.round(g.fO2 * 100);
              const tag =
                o2 === 21 ? "AIR" : o2 === 100 ? "O₂" : o2 < 21 ? `TX${o2}` : `EAN${o2}`;
              return (
                <div key={i} className="deco-row">
                  <div className="deco-marker" />
                  <span className="deco-depth">{s.depth} m</span>
                  <span>{s.duration} min</span>
                  <span style={{ color: "var(--ink-2)" }}>{tag}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
